import { Col, Descriptions, Flex, Row, QRCode } from 'antd';
import { ArrowLeft } from 'lucide-react';
import { Button, Card, Divider, Modal, Spin, Typography } from 'pink-lava-ui';
import { useParams } from 'react-router-dom';
import BookingStatusTag from '@/components/ui/BookingStatusTag';
import dayjs from 'dayjs';
import emptyStateLogo from '@/assets/empty_state.svg';

import { usePreview } from '@/context/previewContext';
import { useBackNavigation } from '@/hooks/useBackNavigation';
import BookingRouteStatus from './components/BookingRouteStatusSection';
import ShipmentListSection from './components/ShipmentListSection';
import TransitListSection from './components/TransitListSection';
import AssignDriverForm from './components/AssignDriverFormSection';
import ChangeLogsButton from '@/components/common/ChangeLogsButton';
import EmptyPlaceholder from '@/components/ui/EmptyPlaceholder';
import { bookingKeys, useBookingById, useBookingShipmentActivity } from '@/features/booking/api/queries';
import { BookingTypeLabel } from '@dc-trans/data-layer';

const BookingDetail = () => {
  const preview = usePreview();
  const { id } = useParams();

  // Enhanced back navigation with intelligent routing
  const { goBack, hasValidPreviousPath, sourceRoute } = useBackNavigation({
    defaultRoute: '/booking-list',
  });

  const bookingByIdQuery = useBookingById(id!);
  const { data: activityResponse } = useBookingShipmentActivity(id!, {
    page: 1,
    limit: 100,
  });

  const bookingDetail = bookingByIdQuery.data?.data;
  const activityList = activityResponse?.data?.items;

  if (bookingByIdQuery.isLoading) {
    return (
      <div className="text-center p-4">
        <Spin size={'large'} />
      </div>
    );
  }

  if (!bookingDetail && !bookingByIdQuery.error) {
    return <EmptyPlaceholder title="Booking Detail" />;
  }

  // Check if error is 403 Forbidden (Access Denied)
  const isAccessDenied = (bookingByIdQuery.error as any)?.response?.status === 403;

  const onOpenQR = () => {
    if (!bookingDetail) return;

    preview({
      content: (
        <div className="flex flex-col gap-4 items-center p-4 pt-12 md:p-0 md:max-w-[480px]">
          <QRCode value={bookingDetail.tms_booking!} size={320} />
          <Descriptions
            column={1}
            size="small"
            className="font-nunito"
            items={[
              { label: 'ID Booking', children: bookingDetail.tms_booking! },
              {
                label: 'Nama Pengemudi',
                children: bookingDetail.assignment?.driver?.name || '-',
              },
              {
                label: 'Kendaraan',
                children: bookingDetail.assignment?.vendor_vehicle?.license_plate || '-',
              },
              {
                label: 'Delivery Date',
                children: bookingDetail.assignment?.delivery_date
                  ? dayjs(bookingDetail.assignment?.delivery_date).format('DD MMMM YYYY')
                  : '-',
              },
              {
                label: 'Shipping Window',
                children:
                  bookingDetail.assignment?.ship_window &&
                  bookingDetail.assignment.ship_window.incoming &&
                  bookingDetail.assignment.ship_window.outgoing
                    ? `${bookingDetail.assignment.ship_window?.incoming}-${bookingDetail.assignment.ship_window?.outgoing}`
                    : '-',
              },
            ]}
          />
        </div>
      ),
      title: 'QR Code',
    });
  };

  const content = bookingDetail ? (
    <Flex gap={16} className="w-full h-full" vertical>
      <Card size="small" className="m-4! mb-0!" rounded="large">
        <div className="flex flex-row gap-2 items-center">
          <Button
            shape="circle"
            type="ghost"
            className="!text-gray-700"
            size="small"
            onClick={goBack}
            title={hasValidPreviousPath ? `Back to ${sourceRoute}` : 'Back to Booking List'}
          >
            <ArrowLeft size={24} />
          </Button>
          <Typography.Heading level={4} className="flex-1">
            Detail {bookingDetail.tms_booking}
          </Typography.Heading>
          <ChangeLogsButton url={`/bookings/${id}/activities`} queryKey={[...bookingKeys.all, id!, 'activities']} />
        </div>
      </Card>
      <Row gutter={[16, 16]} className="px-4">
        <Col xs={24} xl={16}>
          <div className="flex flex-col gap-4 mb-4">
            <Card size="small" rounded="large">
              <div className="flex flex-row items-center">
                <div className={`flex-1 font-nunito`}>
                  <Descriptions
                    column={1}
                    colon={false}
                    classNames={{
                      content:
                        'w-full flex items-end justify-end text-right lg:text-left lg:items-start lg:justify-start font-nunito',
                      label: 'lg:w-[200px] font-nunito',
                    }}
                    items={[
                      {
                        label: 'Status',
                        children: <BookingStatusTag className="text-base!" status={bookingDetail.status!} />,
                      },
                      {
                        label: 'ID Booking',
                        children: bookingDetail.tms_booking!,
                      },
                      {
                        label: 'Tipe Booking',
                        children: BookingTypeLabel[bookingDetail.type as keyof typeof BookingTypeLabel],
                      },
                      {
                        label: 'Nama Vendor',
                        children: bookingDetail.assignment?.vendor?.name,
                      },
                      {
                        label: 'Req. Plant Delivery Date',
                        children: bookingDetail.requested_delivery_date
                          ? dayjs(bookingDetail.requested_delivery_date).format('DD MMMM YYYY')
                          : '-',
                      },
                      {
                        label: 'Req. Shipping Window',
                        children: bookingDetail.req_ship_window
                          ? `${bookingDetail.req_ship_window.incoming} - ${bookingDetail.req_ship_window.outgoing}`
                          : '-',
                      },
                      {
                        label: 'Req. Jenis Kendaraan',
                        children: bookingDetail.req_vehicle_type.name,
                      },
                    ]}
                  />
                  <>
                    <Divider className="lg:hidden! mt-2! mb-4!" />
                    <div className="flex lg:hidden flex-row items-center justify-between">
                      <div>
                        <div className="text-sm font-semibold">QR Code</div>
                        <div className="text-xs text-gray-500">Driver akan scan saat tiba di lokasi.</div>
                      </div>
                      <Button size="small" type="tertiary" onClick={onOpenQR}>
                        Lihat QR Code
                      </Button>
                    </div>
                  </>
                </div>
                <div className="items-center gap-2 flex-col p-4 hidden lg:flex">
                  <QRCode value={bookingDetail.tms_booking!} size={120} />
                  <div className="text-center">
                    <div className="text-base font-semibold">QR Code</div>
                    <div className="text-sm text-gray-500">Driver akan scan saat tiba di lokasi.</div>
                  </div>
                  <Button size="small" type="tertiary" onClick={onOpenQR}>
                    Perbesar QR Code
                  </Button>
                </div>
              </div>
            </Card>
            <div className="block xl:hidden">
              <BookingRouteStatus booking={bookingDetail} activityList={activityList!} />
            </div>

            <Card size="small" rounded="large">
              <p className="text-lg font-semibold">Penugasan</p>
              <p className="text-sm mb-2 text-gray-500">Tugaskan/Ubah Penugasan Driver atau Kendaraan.</p>
              <AssignDriverForm booking={bookingDetail} />
            </Card>
            <div>
              <p className="text-lg font-semibold">Tambah Data</p>
              <p className="text-sm text-gray-500">Lengkapi data untuk Transit, Arrival Claim & Bukti Penerimaan.</p>
            </div>
            <Card size="small" rounded="large">
              <TransitListSection booking={bookingDetail} activityList={activityList!} />
            </Card>
            <ShipmentListSection booking={bookingDetail} activityList={activityList!} />
          </div>
        </Col>
        <Col xs={0} xl={8}>
          <BookingRouteStatus booking={bookingDetail} activityList={activityList!} />
        </Col>
      </Row>
    </Flex>
  ) : null;

  return (
    <>
      {content}
      {isAccessDenied && (
        <Modal
          open={true}
          closable={false}
          maskClosable={false}
          centered
          width={320}
          keyboard={false}
          title="Akses Ditolak"
          footer={
            <div className="flex justify-end">
              <Button block shape="round" onClick={goBack}>
                Kembali ke Daftar Booking
              </Button>
            </div>
          }
        >
          <div className="text-gray-600 text-center flex flex-col items-center gap-6">
            <img src={emptyStateLogo} alt={'access-denied-logo'} className="h-32 w-auto" />
            Penugasan ini tidak valid untuk Anda atau telah dipindahkan ke vendor lain.
          </div>
        </Modal>
      )}
    </>
  );
};

export default BookingDetail;
